import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import i18n from '@risksmart-app/i18n/src/i18n';
import {
  useGetOversightAssessmentsRegister,
  type UseGetOversightAssessmentsRegisterArgs,
} from 'src/hooks/queries/oversight/useGetOversightAssessmentsRegister';

import { oversightAssessmentRegisterUrl } from '@/utils/urls';

import { useGetAssessmentSmartWidgetTableProps } from '../../../assessments/oversight-assessments/config';
import type { OversightAssessmentRegisterFields } from '../../../assessments/oversight-assessments/types';
import { UNRATED } from '../../gigawidget/types';
import { toIsoBounds } from '../../gigawidget/util/filterHelpers';
import { createDataSource } from '../createDataSource';
import {
  dashboardDateRangeClickthroughFilter,
  dateRangeClickthroughFilter,
  defaultClickthroughFilterWithUnrated,
  tagAndDepartmentCategoryGetters,
} from '../dataSourceHelpers';

type GetOversightAssessmentsData = NonNullable<
  ReturnType<typeof useGetOversightAssessmentsRegister>['data']
>;

const data = createDataSource<
  OversightAssessmentRegisterFields,
  UseGetOversightAssessmentsRegisterArgs,
  GetOversightAssessmentsData
>({
  group: 'single',
  parentTypes: [Parent_Type_Enum.OversightAssessment],
  hasAccess: (_, isModuleEnabled) =>
    isModuleEnabled('assessment.subModules.oversight_register_and_findings'),
  useWidgetQuery: useGetOversightAssessmentsRegister,
  useTablePropsHook: (data, options) =>
    useGetAssessmentSmartWidgetTableProps(data?.oversight_assessment, options),
  entityNamePlural: 'compliance_monitoring_assessment_other',
  entityNameSingular: 'compliance_monitoring_assessment_one',
  fields: 'complianceMonitoringAssessment.fields',
  dashboardFilterConfig: {
    tagsFilter: (tags) => ({ tagTypeIds: tags }),
    departmentsFilter: (departments) => ({ departmentTypeIds: departments }),
    dateFilter: (dateRange, precision) => ({
      dateRange: {
        field: 'CreatedAtTimestamp',
        ...toIsoBounds(dateRange, precision),
      },
    }),
    dateClickthroughFilter:
      dashboardDateRangeClickthroughFilter('CreatedAtTimestamp'),
    ownershipFilter: (myItemsFilters) => ({ ownership: myItemsFilters }),
  },
  clickThroughUrl: (filters) => oversightAssessmentRegisterUrl(filters),
  categoryGetters: [
    ...tagAndDepartmentCategoryGetters<OversightAssessmentRegisterFields>(),
    {
      id: 'status',
      name: () => i18n.t('complianceMonitoringAssessment.columns.Status'),
      categoryGetter: (item) => ({
        key: item.Status,
        label: item.StatusLabelled,
      }),
      ratingColourKey: 'assessment_status',
      clickthroughFilter:
        defaultClickthroughFilterWithUnrated('StatusLabelled'),
    },
    {
      id: 'outcome',
      name: () => i18n.t('complianceMonitoringAssessment.columns.Outcome'),
      categoryGetter: (item) => ({
        key: item.Outcome ?? UNRATED,
        label: item.OutcomeLabelled,
      }),
      ratingColourKey: 'compliance_monitoring_assessment_outcome',
      clickthroughFilter:
        defaultClickthroughFilterWithUnrated('OutcomeLabelled'),
    },
    {
      id: 'owner',
      name: () => i18n.t('complianceMonitoringAssessment.columns.Owner'),
      categoryGetter: (item) =>
        item.allOwners.map((owner) => ({ key: owner.id, label: owner.label })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'allOwners',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'contributor',
      name: () => i18n.t('columns.contributors'),
      categoryGetter: (item) =>
        item.allContributors.map((contributor) => ({
          key: contributor.id,
          label: contributor.label,
        })),
      clickthroughFilter: (category) => [
        {
          propertyKey: 'allContributors',
          operator: '=',
          value: category.key,
        },
      ],
    },
    {
      id: 'completionDate',
      name: () =>
        i18n.t('complianceMonitoringAssessment.columns.CompletionDate'),
      categoryGetter: (data) =>
        data.ActualCompletionDate ? new Date(data.ActualCompletionDate) : null,
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: {
          field: 'ActualCompletionDate',
          ...toIsoBounds(dateRange, precision),
        },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('ActualCompletionDate'),
    },
    {
      id: 'startDate',
      name: () => i18n.t('complianceMonitoringAssessment.columns.StartDate'),
      categoryGetter: (data) =>
        data.StartDate ? new Date(data.StartDate) : null,
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: { field: 'StartDate', ...toIsoBounds(dateRange, precision) },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('StartDate'),
    },
    {
      id: 'targetCompletionDate',
      name: () =>
        i18n.t('complianceMonitoringAssessment.columns.TargetCompletionDate'),
      categoryGetter: (data) =>
        data.TargetCompletionDate
          ? new Date(data.TargetCompletionDate as string)
          : null,
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: {
          field: 'TargetCompletionDate',
          ...toIsoBounds(dateRange, precision),
        },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('TargetCompletionDate'),
    },
    {
      id: 'createdDate',
      name: () => i18n.t('columns.created_on'),
      categoryGetter: (data) =>
        data.CreatedAtTimestamp ? new Date(data.CreatedAtTimestamp) : null,
      date: true,
      dashboardDateFilterOverride: (dateRange, precision) => ({
        dateRange: {
          field: 'CreatedAtTimestamp',
          ...toIsoBounds(dateRange, precision),
        },
      }),
      clickthroughFilter: dateRangeClickthroughFilter('CreatedAtTimestamp'),
    },
  ],
});

export default data;
